import React, { useState } from 'react';

const CHANNELS = [
  { value: 'sms',   label: 'Text message (SMS)', icon: '📱', recommended: true },
  { value: 'email', label: 'Email',               icon: '📧' },
  { value: 'both',  label: 'Both',                icon: '📨' },
];

const FREQUENCIES = [
  { value: 'daily',          label: 'Daily check-ins',       description: 'Morning message every day' },
  { value: 'every_other_day', label: 'Every other day',       description: 'Nudges every second day' },
  { value: 'weekly',         label: 'Weekly summary only',   description: 'One message per week' },
];

const TONES = [
  { value: 'gentle',    label: 'Gentle',     description: 'Warm, supportive, no pressure',     emoji: '🌿' },
  { value: 'direct',    label: 'Direct',     description: 'Clear, concise, straight to it',    emoji: '🎯' },
  { value: 'motivating', label: 'Motivating', description: 'Energy, momentum, let\'s go',      emoji: '🔥' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = i.toString().padStart(2, '0');
  const label = i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`;
  return { value: `${h}:00`, label };
});

export default function NudgePrefsStep({ onNext, onBack, initialPrefs }) {
  const [prefs, setPrefs] = useState({
    channel:                  initialPrefs?.channel                  || 'sms',
    frequency:                initialPrefs?.frequency                || 'daily',
    preferred_time:           initialPrefs?.preferred_time           || '08:00',
    tone:                     initialPrefs?.tone                     || 'gentle',
    morning_checkin_enabled:  initialPrefs?.morning_checkin_enabled  ?? true,
    midweek_nudge_enabled:    initialPrefs?.midweek_nudge_enabled    ?? true,
    weekly_summary_enabled:   initialPrefs?.weekly_summary_enabled   ?? true,
  });

  const set = (key, val) => setPrefs(p => ({ ...p, [key]: val }));

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <h2 className="text-xl font-bold text-reset-brown mb-1">How should we reach you?</h2>
      <p className="text-sm text-gray-500 mb-6 leading-relaxed">
        You control everything. Change this any time from your settings.
      </p>

      {/* Channel */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-reset-brown mb-2">Where to send nudges</h3>
        <div className="space-y-2">
          {CHANNELS.map(c => (
            <button
              key={c.value}
              type="button"
              onClick={() => set('channel', c.value)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition
                ${prefs.channel === c.value
                  ? 'border-reset-terracotta bg-orange-50 ring-2 ring-reset-terracotta'
                  : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
            >
              <span className="text-xl">{c.icon}</span>
              <span className="text-sm font-medium text-gray-800">
                {c.label}
                {c.recommended && <span className="ml-2 text-xs text-reset-terracotta font-normal">(recommended)</span>}
              </span>
              {prefs.channel === c.value && (
                <span className="ml-auto text-reset-terracotta">✓</span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Frequency */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-reset-brown mb-2">How often</h3>
        <div className="space-y-2">
          {FREQUENCIES.map(f => (
            <button
              key={f.value}
              type="button"
              onClick={() => set('frequency', f.value)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition
                ${prefs.frequency === f.value
                  ? 'border-reset-terracotta bg-orange-50 ring-2 ring-reset-terracotta'
                  : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
            >
              <div>
                <p className="text-sm font-medium text-gray-800">{f.label}</p>
                <p className="text-xs text-gray-500">{f.description}</p>
              </div>
              {prefs.frequency === f.value && (
                <span className="text-reset-terracotta ml-3 flex-shrink-0">✓</span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Preferred time */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-reset-brown mb-2">Preferred nudge time</h3>
        <select
          value={prefs.preferred_time}
          onChange={e => set('preferred_time', e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        >
          {HOURS.map(h => (
            <option key={h.value} value={h.value}>{h.label}</option>
          ))}
        </select>
        <p className="text-xs text-gray-400 mt-1">Your morning check-in message will arrive around this time.</p>
      </section>

      {/* Tone */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-reset-brown mb-2">Message tone</h3>
        <div className="grid grid-cols-3 gap-2">
          {TONES.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => set('tone', t.value)}
              className={`flex flex-col items-center px-3 py-3 rounded-xl border text-center transition
                ${prefs.tone === t.value
                  ? 'border-reset-terracotta bg-orange-50 ring-2 ring-reset-terracotta'
                  : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
            >
              <span className="text-2xl mb-1">{t.emoji}</span>
              <span className="text-xs font-semibold text-gray-800">{t.label}</span>
              <span className="text-xs text-gray-400 mt-0.5 leading-tight">{t.description}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Nudge type toggles */}
      <section className="mb-6">
        <h3 className="text-sm font-semibold text-reset-brown mb-2">Which nudges to receive</h3>
        <div className="space-y-2">
          {[
            { key: 'morning_checkin_enabled',  label: 'Morning check-in',    desc: 'Daily status + your next right thing' },
            { key: 'midweek_nudge_enabled',    label: 'Mid-week nudge',      desc: 'Wednesday check-in when you need it' },
            { key: 'weekly_summary_enabled',   label: 'Weekly summary',      desc: 'Your week in review every Sunday' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <p className="text-xs text-gray-500">{desc}</p>
              </div>
              <button
                type="button"
                onClick={() => set(key, !prefs[key])}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition
                  ${prefs[key] ? 'bg-reset-terracotta' : 'bg-gray-200'}`}
                aria-checked={prefs[key]}
                role="switch"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition
                    ${prefs[key] ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="flex gap-3 mt-6">
        <button
          onClick={onBack}
          className="flex-1 border border-gray-300 text-gray-600 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
        >
          ← Back
        </button>
        <button
          onClick={() => onNext(prefs)}
          className="flex-[2] bg-reset-terracotta text-white py-3 rounded-xl font-semibold text-sm hover:opacity-90 transition"
        >
          Finish setup →
        </button>
      </div>
    </div>
  );
}
